import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, TextField, FormControl, InputLabel, Select, MenuItem,
    OutlinedInput, Chip, Checkbox, ListItemText, Button, CircularProgress
} from '@mui/material';

export default function CreateCancellationPolicyModal({
    open,
    onClose,
    onSubmit,
    isCreating,
    refundMethodOptions = [],
    loadingRefundMethods = false,
    appliedToOptions = []
}) {
    const [createName, setCreateName] = useState('');
    const [createRefundMethod, setCreateRefundMethod] = useState('');
    const [createAppliedTo, setCreateAppliedTo] = useState([]);

    useEffect(() => {
        if (open) {
            setCreateName('');
            setCreateRefundMethod('');
            setCreateAppliedTo([]);
        }
    }, [open]);

    const handleSubmit = () => {
        onSubmit({
            name: createName,
            refundMethod: createRefundMethod,
            appliedTo: createAppliedTo
        });
    };

    return (
        <Dialog open={open} onClose={() => !isCreating && onClose()} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold' }}>Create Cancellation Policy</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                    <TextField
                        label="Policy Name"
                        fullWidth
                        size="small"
                        value={createName}
                        onChange={(e) => setCreateName(e.target.value)}
                        disabled={isCreating}
                        required
                        placeholder="e.g. Standard Policy"
                    />

                    <FormControl fullWidth size="small" required>
                        <InputLabel id="cancellation-refund-method-label">Refund Method</InputLabel>
                        <Select
                            labelId="cancellation-refund-method-label"
                            value={createRefundMethod}
                            label="Refund Method *"
                            onChange={(e) => setCreateRefundMethod(e.target.value)}
                            disabled={isCreating || loadingRefundMethods}
                            required
                        >
                            {loadingRefundMethods ? (
                                <MenuItem disabled value="">
                                    <em>Loading payment methods...</em>
                                </MenuItem>
                            ) : refundMethodOptions && refundMethodOptions.length > 0 ? (
                                refundMethodOptions.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)
                            ) : (
                                <MenuItem disabled value="">
                                    <em>No active payment methods found</em>
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small" required>
                        <InputLabel id="cancellation-applied-to-label">Applied To</InputLabel>
                        <Select
                            labelId="cancellation-applied-to-label"
                            multiple
                            value={createAppliedTo}
                            onChange={(e) => setCreateAppliedTo(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                            input={<OutlinedInput label="Applied To *" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip key={value} label={value} size="small" />
                                    ))}
                                </Box>
                            )}
                            disabled={isCreating}
                            required
                        >
                            {appliedToOptions.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                    <Checkbox checked={createAppliedTo.indexOf(opt) > -1} />
                                    <ListItemText primary={opt} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} color="inherit" disabled={isCreating}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isCreating || !createName.trim() || !createRefundMethod || createAppliedTo.length === 0}
                    startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isCreating ? 'Creating...' : 'Create Policy'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
